'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import {
  CheckCircle2,
  Clock,
  FilePlus,
  LoaderCircle,
  Lock,
  Mail,
  User,
} from 'lucide-react'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { Suspense, useEffect, useRef, useState, useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import AuthGoogleButton from '@/app/conta/login/_components/auth-google-button'
import { loginUser } from '@/app/conta/login/action'
import { CpfEmailInput } from '@/components/global/cpf-email.-input'
import {
  buildEmailSuggestions,
  EmailSuggestions,
} from '@/components/global/email-input-with-suggestions'
import { PasswordInput } from '@/components/password-input'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useGTM } from '@/hooks/use-gtm'
import { useUnity } from '@/hooks/use-unity'
import { validateCPF } from '@/utils/validate-cpf'

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const EMAIL_SUGGESTIONS_HIDE_DELAY = 150
const letterRegex = /[a-zA-Z]/
const numberRegex = /\d/

const identifierValidation = z.string().superRefine((value, ctx) => {
  if (!value) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Campo obrigatório',
    })
    return
  }

  const looksLikeEmail = value.includes('@') || letterRegex.test(value)

  if (looksLikeEmail) {
    if (!emailRegex.test(value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Email inválido',
      })
    }
    return
  }

  if (numberRegex.test(value.charAt(0))) {
    if (!validateCPF(value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'CPF inválido',
      })
    }
    return
  }

  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: 'Formato inválido. Digite um email ou CPF válido',
  })
})

export const signInFormSchema = z.object({
  identifier: identifierValidation,
  password: z.string().min(1, { message: 'O campo senha é obrigatorio' }),
})

type SigInFormType = z.infer<typeof signInFormSchema>

function SignInFormContent() {
  const [isLoading, startTransition] = useTransition()
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [emailSuggestions, setEmailSuggestions] = useState<string[]>([])
  const suggestionsTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  )

  useEffect(() => () => clearTimeout(suggestionsTimerRef.current), [])

  const { handleSendGTMEvent } = useGTM()
  const { unity } = useUnity()
  const router = useRouter()
  const searchParams = useSearchParams()

  const isSoeducador = unity?.reference === 'U5071E012'

  const images = ['/img/diamond/408.svg', '/img/diamond/409.svg']
  const IMAGE_SWITCH_INTERVAL = 8000 // 3 segundos

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex(prevIndex => (prevIndex + 1) % images.length)
    }, IMAGE_SWITCH_INTERVAL)

    return () => clearInterval(interval)
  }, [])

  const form = useForm<SigInFormType>({
    resolver: zodResolver(signInFormSchema),
    mode: 'all',
    defaultValues: { identifier: '', password: '' },
  })

  const { handleSubmit, control } = form

  // const handleLegacyLogin = async (userEmail: string, userPassword: string) => {
  //   try {
  //     const legacyResult = await loginLegacyAction(userEmail, userPassword)
  //     return legacyResult?.data?.errorMessage === false
  //   } catch {
  //     return false
  //   }
  // }

  const handleSuccessLogin = () => {
    toast.success('Login realizado com sucesso', {
      duration: 5000,
    })
    // Lê o redirectUrl no momento do redirect para garantir que está atualizado
    const redirectUrl = searchParams.get('redirect_url')
    if (redirectUrl) {
      router.push(redirectUrl)
      return
    }
    router.push('/app')
  }

  const handleLoginError = (userIdentifier: string) => {
    const isEmail = emailRegex.test(userIdentifier)
    const identifierType = isEmail ? 'E-mail' : 'CPF'

    toast.error(`${identifierType} ou senha incorretos`, {
      duration: 6000,
    })
  }

  function onSubmit(formData: SigInFormType) {
    startTransition(async () => {
      const { identifier: userIdentifier, password: userPassword } = formData
      const result = await loginUser({
        identifier: userIdentifier,
        password: userPassword,
      })

      const { data } = result

      if (!data?.success) {
        handleLoginError(userIdentifier)
        return
      }

      handleSendGTMEvent({
        eventName: 'login',
        method: 'standard',
      })

      handleSuccessLogin()
    })
  }
  const { identifier, password } = form.formState.errors

  const identifierError = !!identifier?.message
  const passwordError = !!password?.message

  return (
    <div className="flex items-center justify-center">
      <div className="grid w-full max-w-6xl items-center gap-8 lg:grid-cols-2 lg:gap-12">
        {isSoeducador ? (
          <div
            className="hidden h-full space-y-6 overflow-hidden rounded-xl bg-center bg-cover bg-no-repeat shadow-lg transition-all duration-1000 ease-in-out lg:block"
            style={{
              backgroundImage: `url('${images[currentImageIndex]}')`,
            }}
          />
        ) : (
          <div className="hidden h-full space-y-6 overflow-hidden rounded-xl bg-white p-8 shadow-lg lg:block">
            <Link
              className="mb-6 inline-flex items-center gap-2 text-slate-600 text-sm transition-colors duration-200 hover:text-slate-800"
              href="/"
            >
              <span>←</span>
              <span>Voltar para página inicial</span>
            </Link>

            <div className="space-y-6">
              <h2 className="font-semibold text-4xl text-slate-800 leading-tight">
                Olá, seja bem-vindo(a) de volta!
              </h2>

              <p className="max-w-md text-base text-slate-600 leading-relaxed">
                Acesse o ambiente do aluno para visualizar suas matrículas,
                conteúdos e certificados. Curta essa experiência, pois tudo foi
                feito para você.
              </p>

              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <FilePlus className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-500" />
                  <span className="text-slate-700 text-sm">
                    Material de estudos disponibilizados gratuitamente
                  </span>
                </li>

                <li className="flex items-start gap-3">
                  <Clock className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-500" />
                  <span className="text-slate-700 text-sm">
                    Disponível 24 horas por dia
                  </span>
                </li>

                <li className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-500" />
                  <span className="text-slate-700 text-sm">
                    Certificados válidos em todo o Brasil
                  </span>
                </li>

                <li className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-500" />
                  <span className="text-slate-700 text-sm">
                    Plataforma 100% legalizada
                  </span>
                </li>
              </ul>
            </div>
          </div>
        )}

        <div className="rounded-2xl border border-white/20 bg-white/80 p-6 shadow-lg backdrop-blur-xs lg:p-8">
          <div className="mx-auto space-y-6">
            <div className="flex flex-col items-center justify-center gap-4">
              <div className="inline-flex flex-1 items-center justify-center gap-1">
                <Link
                  className="rounded-xl border border-primary-100 px-3 py-1 font-medium text-gray-500 text-sm transition-all duration-300 ease-in-out hover:text-gray-700 focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                  href={
                    searchParams.get('redirect_url')
                      ? `/conta/novo?redirect_url=${encodeURIComponent(searchParams.get('redirect_url') ?? '')}`
                      : '/conta/novo'
                  }
                  scroll={false}
                >
                  Cadastre-se
                </Link>

                <Link
                  className="rounded-xl bg-primary-500 px-3 py-1 font-medium text-sm text-white shadow-xs transition-all duration-300 ease-in-out hover:bg-primary-600 focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                  href="/conta/login"
                  scroll={false}
                >
                  Já possuo cadastro
                </Link>
              </div>

              {isSoeducador && (
                <div className="space-y-4">
                  <h2 className="font-semibold text-4xl text-slate-800 leading-tight">
                    Olá, seja <br />
                    bem-vindo(a) de volta!
                  </h2>
                  <p className="max-w-md text-base text-slate-600 leading-relaxed">
                    Acesse o ambiente do aluno para visualizar suas matrículas,
                    conteúdos e certificados. Curta essa experiência, pois tudo
                    foi feito para você.
                  </p>
                </div>
              )}
            </div>

            <h2 className="font-semibold text-slate-800 text-xl leading-tight">
              Login
            </h2>

            <Form {...form}>
              <form method="post"
                className="space-y-5"
                noValidate
                onSubmit={handleSubmit(onSubmit)}
              >
                <FormField
                  control={control}
                  name="identifier"
                  render={({ field }) => (
                    <FormItem className="relative space-y-2">
                      <FormLabel className="flex items-center gap-2 font-medium text-slate-700 text-sm">
                        <Mail className="h-4 w-4" />
                        E-mail ou CPF
                      </FormLabel>
                      <FormControl>
                        <CpfEmailInput
                          className="h-11 rounded-xl border-2 border-slate-200 px-4 text-sm transition-all duration-200 placeholder:text-slate-400 focus:border-primary-400 focus:ring-4 focus:ring-primary-100 data-[error=true]:border-red-500 data-[error=true]:focus:ring-red-100"
                          data-error={identifierError}
                          disabled={isLoading}
                          placeholder="Digite seu e-mail ou CPF"
                          {...field}
                          onBlur={e => {
                            field.onBlur()
                            clearTimeout(suggestionsTimerRef.current)
                            suggestionsTimerRef.current = setTimeout(
                              () => setEmailSuggestions([]),
                              EMAIL_SUGGESTIONS_HIDE_DELAY
                            )
                          }}
                          onChange={e => {
                            field.onChange(e)
                            setEmailSuggestions(
                              buildEmailSuggestions(e.target.value)
                            )
                          }}
                        />
                      </FormControl>
                      <FormMessage className="text-red-600 text-xs" />
                      <EmailSuggestions
                        onSelect={value => {
                          form.setValue('identifier', value, {
                            shouldValidate: true,
                            shouldDirty: true,
                          })
                          setEmailSuggestions([])
                        }}
                        suggestions={emailSuggestions}
                      />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="password"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="flex items-center gap-2 font-medium text-slate-700 text-sm">
                        <Lock className="h-4 w-4" />
                        Senha
                      </FormLabel>
                      <FormControl>
                        <PasswordInput
                          className="h-11 rounded-xl border-2 border-slate-200 px-4 text-sm transition-all duration-200 placeholder:text-slate-400 focus:border-primary-400 focus:ring-4 focus:ring-primary-100 data-[error=true]:border-red-500 data-[error=true]:focus:ring-red-100"
                          data-error={passwordError}
                          disabled={isLoading}
                          placeholder="Digite sua senha"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-red-600 text-xs" />
                    </FormItem>
                  )}
                />

                <Button
                  className="h-11 w-full rounded-xl bg-primary-400 font-semibold text-white shadow-lg transition-all duration-200 hover:bg-primary-500 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={isLoading}
                  type="submit"
                >
                  {isLoading ? (
                    <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <User className="mr-2 h-4 w-4" />
                  )}
                  {isLoading ? 'Entrando...' : 'Continuar'}
                </Button>

                <div className="flex items-center justify-center gap-1">
                  <p className="text-slate-600 text-sm">Esqueceu sua senha?</p>
                  <Link
                    className="font-medium text-primary-400 text-sm underline transition-colors duration-200 hover:text-primary-500"
                    href="/conta/lembrar-senha"
                  >
                    Clique aqui
                  </Link>
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-slate-200 border-t" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="bg-white px-4 font-medium text-slate-500">
                      ou
                    </span>
                  </div>
                </div>

                <AuthGoogleButton />
              </form>
            </Form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function SignInForm() {
  return (
    <Suspense fallback={<div>Carregando...</div>}>
      <SignInFormContent />
    </Suspense>
  )
}
